import { create } from "zustand";
import faker from "faker";
import { decodeTokenService } from "../services/decodeTokenService";
import { vPagesUsersService } from "../services/vfPages/";
import { WebsiteDeleteResponse } from "../interfaces/websites";
import { Website, WebsitesState } from "../interfaces/websites";
import { WebsiteType, WebsiteStatus } from "../types/websites";
import { getListOfFunnels } from "../services/generativeAI/generativeAIAxios/generateCopysAxios";
import { cachedRequest, clearCachedRequest } from "../utils/requestCache";

const WEBSITES_TTL_MS = 60_000;
let initializePromise: Promise<void> | null = null;
let initializeGeneration = 0;

const firstNonBlank = (...values: unknown[]) =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";

const arrayValues = (value: unknown) => (Array.isArray(value) ? value : []);

const normalizeStringArray = (value: unknown) =>
  arrayValues(value).map((item) => String(item || "").trim()).filter(Boolean);

const normalizeWebsiteProducts = (site: any): Website["products"] => {
  const rows = [
    ...arrayValues(site.products),
    ...arrayValues(site.product_offers),
    ...arrayValues(site.productOffers),
  ];
  const seen = new Set<string>();
  return rows
    .map((item: any) => {
      const productId = firstNonBlank(item?.product_id, item?.productId, item?.product_ref, item?.productRef, item?.id);
      const type = firstNonBlank(item?.type, item?.product_type, item?.productType) || "PRINCIPAL";
      if (!productId) return null;
      const key = `${productId}:${type}`;
      if (seen.has(key)) return null;
      seen.add(key);
      return { ...item, product_id: productId, productId, type };
    })
    .filter((item): item is Website["products"][number] => Boolean(item));
};

const resolveSaleAngleComplementaryAngles = (saleAngle: any) =>
  normalizeStringArray(
    saleAngle?.complementary_angles ??
      saleAngle?.complementaryAngles ??
      saleAngle?.secondary_angles ??
      saleAngle?.secondaryAngles
  );

const resolveWebsiteUrl = (site: any) => {
  const candidateUrls = [
    ...arrayValues(site.candidate_urls),
    ...arrayValues(site.candidateUrls),
  ];
  return firstNonBlank(
    site.url,
    site.public_url,
    site.publicUrl,
    site.primary_public_url,
    site.primaryPublicUrl,
    site.legacy_onlinesl_url,
    site.legacyOnlineslUrl,
    ...candidateUrls
  );
};

const fetchWebsites = async (): Promise<Website[]> => {
  // const userEmail = decodeTokenService.getEmailFromToken();
  // if (!userEmail) return []

  const userId = decodeTokenService.getUserIdFromToken() || "";
  const websitesData = await cachedRequest(`websites:${userId}:list`, WEBSITES_TTL_MS, getListOfFunnels);
  const websitesWithUrls = await Promise.all(
    websitesData
      // .filter((site: any) => {
      //   return site.status === "PUBLISH";
      // })
      .map(async (site: any) => {
        try {
          const publicUrl = firstNonBlank(site.public_url, site.publicUrl, site.primary_public_url, site.primaryPublicUrl);
          const candidateUrls = [
            ...arrayValues(site.candidate_urls),
            ...arrayValues(site.candidateUrls),
          ].map((url) => String(url || "").trim()).filter(Boolean);
          const websiteProducts = normalizeWebsiteProducts(site);
          return {
            userId: userId,
            projectId: "",
            websiteId: site.id.toString(),
            name: site.brand_name || site.name || site.product?.name || "",
            productName: site.product?.name || site.name || site.brand_name || "",
            type: (site.type as WebsiteType) || "Landing Page",
            status: "Online" as WebsiteStatus,
            url: resolveWebsiteUrl(site),
            publicUrl: publicUrl || null,
            primaryPublicUrl: firstNonBlank(site.primary_public_url, site.primaryPublicUrl) || null,
            primaryHost: firstNonBlank(site.primary_host, site.primaryHost) || null,
            legacyOnlineslUrl: firstNonBlank(site.legacy_onlinesl_url, site.legacyOnlineslUrl) || null,
            candidateUrls,
            sales_angle: site.sale_angle?.name || "",
            sales_angle_description: site.sale_angle?.description || site.product?.description || "",
            sales_angle_complementary_angles: resolveSaleAngleComplementaryAngles(site.sale_angle),
            visits: 0,
            salesSummary: site.sales_summary
              ? {
                salesCount: Number(site.sales_summary.sales_count || 0),
                revenuePaid: site.sales_summary.revenue_paid || 0,
                currencyCode: site.sales_summary.currency_code || undefined,
                conversionRate: site.sales_summary.conversion_rate ?? null,
              }
              : null,
            products: websiteProducts.length
              ? websiteProducts
              : site.product?.id
                ? [{ product_id: site.product.id, productId: site.product.id, type: "PRINCIPAL" }]
                : [],
            productImage: site.product?.image || "",
            generated: typeof site.sections_generated === "boolean" ? site.sections_generated : undefined,
            generationStatus: site.generation_status ?? null,
            failureStage: site.failure_stage ?? null,
            failureReason: site.failure_reason ?? null,
            failureCode: site.failure_code ?? null,
            retryAvailable: Boolean(site.retry_available),
            recoveryAction: site.recovery_action ?? null,
            automaticOrderShopify:
              site.automatic_order_shopify ??
              site.automaticOrderShopify ??
              site.configurations?.automatic_order_shopify ??
              site.configurations?.automaticOrderShopify ??
              site.shopify_integration?.automatic_order_shopify ??
              site.shopifyIntegration?.automaticOrderShopify ??
              null,
            automaticOrderDropi:
              site.automatic_order_dropi ??
              site.automaticOrderDropi ??
              site.configurations?.automatic_order_dropi ??
              site.configurations?.automaticOrderDropi ??
              null,
            shopifyIntegrationStatus:
              site.shopify_integration_state ??
              site.shopifyIntegrationState ??
              site.shopify_integration?.status ??
              site.shopifyIntegration?.status ??
              null,
            dropiIntegrationStatus:
              site.dropi_integration_state ??
              site.dropiIntegrationState ??
              null,
            conversionRate: 0,
            theme: "Default Theme",
            lastEdited: new Date(site.updated_at || site.created_at).toISOString(),
            createdAt: new Date(site.created_at).toISOString(),
            configurations: {
              enabled: false,
              name_funnel: "",
              ...(site.configurations || {}),
            },
            idSequence: site.id_sequence_owner_id || 0,
          };
        } catch (error) {
          console.error("Error processing site:", site, error);
          throw error;
        }
      })
  );

  return websitesWithUrls;
};

export const useWebsitesStore = create<WebsitesState>((set) => ({
  projectId: null,
  websites: [],
  tempChanges: {},
  hasChanges: false,
  userId: null,
  isLoading: false,
  loadError: null,

  addWebsite: (website) =>
    set((state) => ({
      websites: [
        ...state.websites,
        {
          ...website,
          id: faker.datatype.uuid(),
          createdAt: new Date().toISOString(),
          lastEdited: new Date().toISOString(),
        },
      ],
      hasChanges: true,
    })),
  deleteWebsite: async (id) => {
    try {
      // Llamada al servicio para eliminar el sitio web
      const userId = decodeTokenService.getUserIdFromToken() || "";

      await vPagesUsersService.deleteWebsite(userId, id);
      // if (response.success) {
      //   set((state) => ({
      //     websites: state.websites.filter(
      //       (website) => website.websiteId !== id
      //     ),
      //     hasChanges: true,
      //   }));
      // } else {
      //   throw new Error("Error al eliminar el sitio web");
      // }
      return "";
    } catch (error) {
      console.error("Error al eliminar el sitio web:", error);
      throw error; // Re-lanzamos el error para manejarlo en el componente
    }
  },
  setTempChanges: (changes) =>
    set(() => ({
      tempChanges: changes,
      hasChanges: Object.keys(changes).length > 0,
    })),
  saveChanges: () =>
    set((state) => {
      const updatedWebsites = state.websites.map((website) => ({
        ...website,
        ...state.tempChanges[website.websiteId],
        lastEdited: new Date().toISOString(),
      }));

      return {
        websites: updatedWebsites,
        tempChanges: {},
        hasChanges: false,
      };
    }),
  discardChanges: () =>
    set({
      websites: [],
      tempChanges: {},
      hasChanges: false,
    }),

  initialize: async () => {
    if (initializePromise) return initializePromise;
    const generation = initializeGeneration;
    set({ isLoading: true, loadError: null });
    let currentPromise: Promise<void> | null = null;
    currentPromise = (async () => {
      try {
        const userId = decodeTokenService.getUserIdFromToken() || "";
        const websites = await fetchWebsites();
        if (generation !== initializeGeneration) return;
        set({
          userId,
          websites,
          isLoading: false,
          loadError: null,
        });
      } catch (error) {
        console.error("Error initializing user:", error);
        if (generation !== initializeGeneration) return;
        set({
          isLoading: false,
          loadError: "No pudimos cargar tus embudos. Revisa la conexión e intenta de nuevo.",
        });
      } finally {
        if (initializePromise === currentPromise) {
          initializePromise = null;
        }
      }
    })();
    initializePromise = currentPromise;
    return initializePromise;
  },

  projectActive: async () => {
    // const userId = await userActive();
    // const userId = decodeTokenService.getUserIdFromToken() || "";
    // if (!userId) throw new Error("Usuario no encontrado");
    // const projectId = await projectActive(userId);
    // if (!projectId) throw new Error("Proyecto no encontrado");
    return "";
  },
}));

export const invalidateWebsitesCache = () => {
  initializeGeneration += 1;
  initializePromise = null;
  clearCachedRequest("websites:");
};
